// GroupAppAllowlistManager — admin page at /settings/group-app-allowlist.
// Backs the skip_if_in_group_app_allowlist suppression tier (v1.85.0): per
// (rule, tenant) (group, application) pairs whose matches are suppressed when the
// signing user is a member of the allowlisted Entra group AND the match's application
// is on that group's allowlist. The pipeline matches the GROUP by its verbatim display
// name (the authoritative key) and the APP by GUID ('*' = any app). There is NO "any
// group" wildcard. Read-open, admin-write. Soft-delete (enabled=false). Mirrors
// UserAppPairAllowlistManager (rule tabs / resize / paginate / sentinel health +
// reconcile + Known-Apps app picker), with the user input replaced by an Entra-groups
// picker sourced from the SilverM365EntraGroups ATS mirror (with a manual-entry fallback).

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  UserGroupIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowsRightLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchGroupAppAllowlist,
  fetchGroupAppAllowlistSentinelHealth,
  addGroupAppAllowlistEntry,
  updateGroupAppAllowlistEntry,
  removeGroupAppAllowlistEntry,
  reconcileGroupAppAllowlistSentinel,
  fetchTenants,
  fetchEntraServicePrincipals,
  fetchEntraIdentityInventory,
  fetchDetectionRules,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import SearchableSelect from './ui/SearchableSelect';
import Pagination from './Pagination';

// "BA: Sign-in from Non-Compliant Device" — the launch consumer; kept as the
// graceful-degradation fallback when the (admin-only) detection-rules list is
// unavailable. The enabled rule set is otherwise discovered from each rule's
// skip_if_in_group_app_allowlist toggle.
const LAUNCH_RULE_ID = 'rule_85658b07-7d12-4d36-a9d3-78c85500964c';
const LAUNCH_FALLBACK = { ruleId: LAUNCH_RULE_ID, ruleName: 'BA: Sign-in from Non-Compliant Device' };
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const todayStr = () => new Date().toISOString().slice(0, 10);

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}

function validateAddForm({ tenantId, groupDisplayName, applicationId, note }) {
  if (!tenantId) return 'Tenant is required.';
  if (tenantId !== '*' && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId)) {
    return "Tenant must be a GUID or '*'.";
  }
  if (!groupDisplayName) return 'Group is required (the verbatim Entra group display name).';
  if (groupDisplayName === '*') {
    return "There is no 'any group' option — pick a specific group. Use excluded tenants to disable a rule.";
  }
  if (groupDisplayName.includes('|')) return "Group display name cannot contain '|'.";
  if (groupDisplayName.length > 256) return 'Group display name exceeds 256 chars.';
  if (!applicationId) return 'Application is required (app GUID or *).';
  if (applicationId !== '*' && !GUID_REGEX.test(applicationId)) {
    return "Application must be a GUID or '*'.";
  }
  if (note && note.length > 2000) return 'Note exceeds 2000 chars.';
  return null;
}

export default function GroupAppAllowlistManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [allowlistRules, setAllowlistRules] = useState([]);
  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [showDisabled, setShowDisabled] = useState(true);

  const [health, setHealth] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [confirmReconcile, setConfirmReconcile] = useState(false);

  // Entra service principals (= Enterprise Apps) for the application picker, keyed off
  // the selected tenant. app_id == the appId on graph_signin alerts (the match key).
  const [entraApps, setEntraApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsSnapshot, setAppsSnapshot] = useState('');
  // Entra groups (for the group picker), keyed off the selected tenant.
  const [entraGroups, setEntraGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsSnapshot, setGroupsSnapshot] = useState('');

  // Add form
  const [formTenantId, setFormTenantId] = useState('');
  // groupMode: 'pick' | 'manual'
  const [formGroupMode, setFormGroupMode] = useState('pick');
  const [formGroupName, setFormGroupName] = useState('');
  // appMode: 'pick' | 'manual' | 'any'
  const [formAppMode, setFormAppMode] = useState('pick');
  const [formAppId, setFormAppId] = useState('');
  const [formAppName, setFormAppName] = useState('');
  const [formNote, setFormNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Inline edit (note + display name)
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [editAppName, setEditAppName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [sortKey, setSortKey] = useState('addedUtc');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: Tenant, Group, Application, Enabled, Note, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([180, 260, 280, 100, 200, 150, 120, 110]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const loadMeta = async () => {
    try {
      const tenantData = await fetchTenants();
      setTenants(Array.isArray(tenantData) ? tenantData : []);
    } catch { /* best-effort */ }
    let rules = [];
    try {
      const all = await fetchDetectionRules();
      rules = (Array.isArray(all) ? all : [])
        .filter((r) => r.skipIfInGroupAppAllowlist === true)
        .map((r) => ({ ruleId: r.ruleId, ruleName: r.ruleName || r.ruleId }));
    } catch { /* 403 / transient — fall back below */ }
    if (rules.length === 0) rules = [LAUNCH_FALLBACK];
    rules.sort((a, b) => (a.ruleName || '').localeCompare(b.ruleName || ''));
    setAllowlistRules(rules);
    setSelectedRuleId((prev) =>
      prev || rules.find((r) => r.ruleId === LAUNCH_RULE_ID)?.ruleId || rules[0].ruleId
    );
  };

  const loadEntries = async (ruleId) => {
    if (!ruleId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchGroupAppAllowlist(ruleId, 'all');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load group/app pair allowlist');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const h = await fetchGroupAppAllowlistSentinelHealth();
      setHealth(h);
    } catch { /* best-effort */ }
  };

  useEffect(() => {
    loadMeta();
    loadHealth();
    const intervalId = setInterval(loadHealth, 30_000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    loadEntries(selectedRuleId);
    setCurrentPage(1);
  }, [selectedRuleId]);

  // Load the tenant's Entra service principals (= Enterprise Apps) for the application
  // picker (today→yesterday snapshot fallback). The '*' cross-tenant case has no single
  // snapshot, so the picker stays empty and the analyst uses Enter-GUID / Any-app.
  useEffect(() => {
    let cancelled = false;
    setEntraApps([]);
    setAppsSnapshot('');
    if (!formTenantId || formTenantId === '*') return;
    setAppsLoading(true);
    (async () => {
      try {
        const td = todayStr();
        let apps = await fetchEntraServicePrincipals(formTenantId, { snapshotDate: td });
        let effectiveDate = td;
        if (!Array.isArray(apps) || apps.length === 0) {
          const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
          const a2 = await fetchEntraServicePrincipals(formTenantId, { snapshotDate: yesterday });
          if (Array.isArray(a2) && a2.length > 0) { apps = a2; effectiveDate = yesterday; }
        }
        if (cancelled) return;
        const list = Array.isArray(apps) ? apps : [];
        setEntraApps(list);
        setAppsSnapshot(list.length ? effectiveDate : '');
      } catch {
        if (!cancelled) setEntraApps([]);
      } finally {
        if (!cancelled) setAppsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formTenantId]);

  // Load Entra groups for the group picker (today→yesterday snapshot fallback,
  // mirroring EntraIdentityPage). Only a real tenant has a groups snapshot; for the
  // '*' cross-tenant case there's no single tenant to pull from, so fall back to manual.
  useEffect(() => {
    let cancelled = false;
    setEntraGroups([]);
    setGroupsSnapshot('');
    if (!formTenantId || formTenantId === '*') {
      setFormGroupMode('manual');
      return;
    }
    setFormGroupMode('pick');
    setGroupsLoading(true);
    (async () => {
      try {
        const td = todayStr();
        let groups = await fetchEntraIdentityInventory(formTenantId, { snapshotDate: td, collection: 'groups' });
        let effectiveDate = td;
        if ((!Array.isArray(groups) || groups.length === 0)) {
          const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
          const g2 = await fetchEntraIdentityInventory(formTenantId, { snapshotDate: yesterday, collection: 'groups' });
          if (Array.isArray(g2) && g2.length > 0) { groups = g2; effectiveDate = yesterday; }
        }
        if (cancelled) return;
        const list = Array.isArray(groups) ? groups : [];
        setEntraGroups(list);
        setGroupsSnapshot(list.length ? effectiveDate : '');
        if (list.length === 0) setFormGroupMode('manual');
      } catch {
        if (!cancelled) { setEntraGroups([]); setFormGroupMode('manual'); }
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formTenantId]);

  const tenantNameById = useMemo(() => {
    const map = {};
    tenants.forEach((t) => { if (t.TenantID) map[t.TenantID] = t.FriendlyName || t.TenantID; });
    return map;
  }, [tenants]);

  const sortedTenants = useMemo(
    () => [...tenants].sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || '')),
    [tenants]
  );

  const sortedApps = useMemo(
    () => [...entraApps]
      .filter((a) => a.appId)
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')),
    [entraApps]
  );

  const sortedGroups = useMemo(
    () => [...entraGroups]
      .filter((g) => g.displayName)
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')),
    [entraGroups]
  );

  const filtered = useMemo(() => {
    let r = rows;
    if (!showDisabled) r = r.filter((x) => x.enabled !== false);
    if (tenantFilter !== 'all') r = r.filter((x) => x.tenantId === tenantFilter || x.tenantId === '*');
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      r = r.filter((x) =>
        (x.groupDisplayName && x.groupDisplayName.toLowerCase().includes(q)) ||
        (x.applicationId && x.applicationId.toLowerCase().includes(q)) ||
        (x.applicationDisplayName && x.applicationDisplayName.toLowerCase().includes(q)) ||
        (x.note && x.note.toLowerCase().includes(q)) ||
        (x.addedBy && x.addedBy.toLowerCase().includes(q))
      );
    }
    return r;
  }, [rows, showDisabled, tenantFilter, filterQuery]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av === bv) return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      return sortDir === 'asc' ? 1 : -1;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, tenantFilter, showDisabled, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-700 ml-1">{'↕'}</span>;
    return sortDir === 'asc' ? <ChevronUpIcon className="inline h-3 w-3 ml-1" /> : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
  };

  const resetAddForm = () => {
    setFormTenantId('');
    setFormGroupMode('pick');
    setFormGroupName('');
    setFormAppMode('pick');
    setFormAppId('');
    setFormAppName('');
    setFormNote('');
  };

  const handleAppPick = (appId) => {
    setFormAppId(appId);
    const match = entraApps.find((a) => (a.appId || '').toLowerCase() === (appId || '').toLowerCase());
    setFormAppName(match ? (match.displayName || '') : '');
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const effectiveGroup = formGroupName.trim();
    const effectiveAppId = formAppMode === 'any' ? '*' : formAppId.trim().toLowerCase();
    const effectiveAppName = formAppMode === 'any' ? '' : formAppName.trim();

    const validationErr = validateAddForm({
      tenantId: formTenantId,
      groupDisplayName: effectiveGroup,
      applicationId: effectiveAppId,
      note: formNote,
    });
    if (validationErr) { setAddError(validationErr); return; }

    setAdding(true);
    setAddError(null);
    try {
      const created = await addGroupAppAllowlistEntry({
        ruleId:                 selectedRuleId,
        tenantId:               formTenantId,
        groupDisplayName:       effectiveGroup,
        applicationId:          effectiveAppId,
        applicationDisplayName: effectiveAppName,
        note:                   formNote.trim(),
        enabled:                true,
      });
      setRows((prev) => {
        const ix = prev.findIndex((r) => r.partitionKey === created.partitionKey && r.rowKey === created.rowKey);
        if (ix >= 0) { const next = [...prev]; next[ix] = created; return next; }
        return [created, ...prev];
      });
      resetAddForm();
      loadHealth();
    } catch (err) {
      setAddError(err.message || 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(`${row.partitionKey}::${row.rowKey}`);
    setEditNote(row.note || '');
    setEditAppName(row.applicationDisplayName || '');
  };

  const cancelEdit = () => { setEditingId(null); setEditNote(''); setEditAppName(''); setEditError(null); };

  const handleSaveEdit = async (row) => {
    if (editNote && editNote.length > 2000) { setEditError('Note exceeds 2000 chars.'); return; }
    if (editAppName && editAppName.length > 256) { setEditError('Display name exceeds 256 chars.'); return; }
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateGroupAppAllowlistEntry(row.partitionKey, row.rowKey, {
        note: editNote.trim(),
        applicationDisplayName: editAppName.trim(),
      });
      setRows((prev) => prev.map((x) => (x.partitionKey === row.partitionKey && x.rowKey === row.rowKey ? { ...x, ...updated } : x)));
      cancelEdit();
      loadHealth();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleEnabled = async (row) => {
    setTogglingId(`${row.partitionKey}::${row.rowKey}`);
    try {
      const updated = await updateGroupAppAllowlistEntry(row.partitionKey, row.rowKey, { enabled: !row.enabled });
      setRows((prev) => prev.map((x) => (x.partitionKey === row.partitionKey && x.rowKey === row.rowKey ? { ...x, ...updated } : x)));
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to toggle entry');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemove = async (row) => {
    setRemoving(true);
    try {
      await removeGroupAppAllowlistEntry(row.partitionKey, row.rowKey);
      // Soft-delete: row is retained with enabled=false.
      setRows((prev) => prev.map((x) => (x.partitionKey === row.partitionKey && x.rowKey === row.rowKey ? { ...x, enabled: false } : x)));
      setConfirmRemove(null);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to disable entry');
    } finally {
      setRemoving(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      await reconcileGroupAppAllowlistSentinel(selectedRuleId);
      setConfirmReconcile(false);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to reconcile sentinel');
    } finally {
      setReconciling(false);
    }
  };

  const activeRuleName = allowlistRules.find((r) => r.ruleId === selectedRuleId)?.ruleName || selectedRuleId;

  const canSubmit = isAdmin && !adding && formTenantId &&
    formGroupName.trim() &&
    (formAppMode === 'any' || formAppId.trim());

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <UserGroupIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">Group / Application Pair Allowlist</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Benign <span className="text-gray-400">(group membership, application)</span> pairs for{' '}
          <span className="font-mono text-gray-400">{activeRuleName}</span>. A match is suppressed when the signing user
          is a member of the allowlisted Entra <strong>group</strong> AND the match's <strong>application</strong> is on
          that group's list. The group is matched by its <strong>verbatim display name</strong>; the app by its GUID
          (use <span className="font-mono text-gray-400">*</span> for "any app"). Group membership is refreshed{' '}
          <strong>once daily</strong>, so a newly-added member starts being suppressed at the next daily run. There is no
          "any group" option. A known-malicious IP still alerts. Tenant + Group + Application are the row's identity and
          immutable — only the note and app display name are editable; delete soft-disables (the row is kept with{' '}
          <span className="font-mono text-gray-400">enabled=false</span>).
        </p>
      </div>

      {/* Rule tabs */}
      <div className="mb-5 border-b border-gray-800">
        <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Detection rule">
          {allowlistRules.map((r) => {
            const active = r.ruleId === selectedRuleId;
            return (
              <button
                key={r.ruleId}
                role="tab"
                aria-selected={active}
                onClick={() => setSelectedRuleId(r.ruleId)}
                className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-blue-500 text-blue-300 bg-gray-800/40'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {r.ruleName}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sentinel health banner */}
      {health?.outboxPending && (
        <div className="mb-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            Pending sync since {formatDate(health.outboxEnqueuedUtc)} ({health.outboxAttemptCount} attempt{health.outboxAttemptCount !== 1 ? 's' : ''}).
            The detection pipeline may not yet see recent changes. The retry timer runs every ~30&nbsp;s.
          </span>
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} className="ml-auto underline hover:no-underline text-amber-200">
              Reconcile now
            </button>
          )}
        </div>
      )}

      {confirmReconcile && (
        <div className="mb-4 p-3 bg-gray-900 border border-gray-700 rounded-lg text-sm flex items-center gap-3">
          <ArrowsRightLeftIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <span className="text-gray-300">Force-bump the sentinel? This recomputes the version monotonically and clears any pending outbox row.</span>
          <button onClick={handleReconcile} disabled={reconciling} className="btn-primary px-3 py-1 text-xs ml-auto">
            {reconciling ? 'Reconciling…' : 'Confirm'}
          </button>
          <button onClick={() => setConfirmReconcile(false)} className="btn-secondary px-3 py-1 text-xs">Cancel</button>
        </div>
      )}

      {/* Add form */}
      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">
            Add (Group, Application) Pair
            <span className="text-gray-500 font-normal"> — {activeRuleName}</span>
          </h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Tenant</label>
                <select value={formTenantId} onChange={(e) => setFormTenantId(e.target.value)} className="input-field h-10 py-0" required>
                  <option value="">— Tenant —</option>
                  <option value="*">All tenants (*)</option>
                  {sortedTenants.map((t) => (
                    <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
                  ))}
                </select>
              </div>
              {/* Group picker */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">
                  Entra Group
                  {formTenantId && formTenantId !== '*' && (
                    <span className="text-gray-600 font-normal">
                      {groupsLoading ? ' — loading…' : groupsSnapshot ? ` — ${sortedGroups.length} groups (${groupsSnapshot})` : ' — no snapshot'}
                    </span>
                  )}
                </label>
                <div className="flex items-center gap-3 mb-2 text-xs text-gray-400">
                  {[
                    { v: 'pick', label: 'Pick group' },
                    { v: 'manual', label: 'Enter name' },
                  ].map((m) => (
                    <label key={m.v} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="groupMode" value={m.v} checked={formGroupMode === m.v}
                        onChange={() => { setFormGroupMode(m.v); setFormGroupName(''); }}
                        className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                      {m.label}
                    </label>
                  ))}
                </div>
                {formGroupMode === 'pick' ? (
                  <SearchableSelect
                    value={formGroupName}
                    onChange={setFormGroupName}
                    options={sortedGroups.map((g) => ({ value: g.displayName, label: g.displayName }))}
                    disabled={groupsLoading || sortedGroups.length === 0}
                    placeholder={sortedGroups.length === 0 ? '— No groups (pick a tenant) —' : 'Search groups…'}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="Verbatim Entra group display name"
                    value={formGroupName}
                    onChange={(e) => setFormGroupName(e.target.value)}
                    className="input-field h-10 py-0 text-xs"
                  />
                )}
              </div>
            </div>

            {/* Application picker */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">
                  Application
                  {formTenantId && formTenantId !== '*' && (
                    <span className="text-gray-600">
                      {appsLoading ? ' — loading…' : appsSnapshot ? ` — ${sortedApps.length} apps (${appsSnapshot})` : ' — no snapshot'}
                    </span>
                  )}
                </label>
                <div className="flex items-center gap-3 mb-2 text-xs text-gray-400">
                  {[
                    { v: 'pick', label: 'Pick app' },
                    { v: 'manual', label: 'Enter GUID' },
                    { v: 'any', label: 'Any app (*)' },
                  ].map((m) => (
                    <label key={m.v} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="appMode" value={m.v} checked={formAppMode === m.v}
                        onChange={() => { setFormAppMode(m.v); setFormAppId(''); setFormAppName(''); }}
                        className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                      {m.label}
                    </label>
                  ))}
                </div>
                {formAppMode === 'pick' && (
                  <SearchableSelect
                    value={formAppId}
                    onChange={handleAppPick}
                    options={sortedApps.map((a) => ({ value: a.appId, label: a.displayName || a.appId }))}
                    disabled={appsLoading || sortedApps.length === 0}
                    placeholder={sortedApps.length === 0 ? '— No apps (pick a tenant) —' : 'Search apps…'}
                  />
                )}
                {formAppMode === 'manual' && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input type="text" placeholder="App GUID (38aa3b87-…)" value={formAppId}
                      onChange={(e) => setFormAppId(e.target.value)} className="input-field h-10 py-0 font-mono text-xs flex-1" />
                    <input type="text" placeholder="Display name (optional)" value={formAppName}
                      onChange={(e) => setFormAppName(e.target.value)} className="input-field h-10 py-0 flex-1" />
                  </div>
                )}
                {formAppMode === 'any' && (
                  <p className="text-xs text-amber-300 h-10 flex items-center">Any application will match for this group (use sparingly).</p>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Note</label>
                <input
                  type="text"
                  placeholder="e.g. Contractor CA exception group — One Outlook Web is benign"
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  className="input-field h-10 py-0"
                />
              </div>
              <div className="flex-shrink-0 lg:pt-[26px]">
                <button type="submit" disabled={!canSubmit} className="btn-primary flex items-center gap-2 whitespace-nowrap">
                  {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                  Add Pair
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              The group is matched by its <strong>display name</strong> — if the group is renamed in Entra, this row stops
              matching silently (soft-disable and re-add it). The app is matched by <strong>GUID</strong>. Re-adding an
              existing pair is an idempotent upsert that also re-enables a soft-disabled row.
            </p>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add, edit, or disable entries.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => loadEntries(selectedRuleId)} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by group, app, note, added by..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} className="input-field sm:w-64">
            <option value="all">All tenants</option>
            <option value="*">Cross-tenant (*) only</option>
            {sortedTenants.map((t) => (
              <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
            />
            Show disabled
          </label>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isAdmin && (
            <button
              onClick={() => setConfirmReconcile(true)}
              disabled={reconciling}
              className="btn-secondary flex items-center gap-1.5 text-xs"
              title="Force-bump the sentinel (recovery)"
            >
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
              Reconcile sentinel
            </button>
          )}
          <span>{filtered.length} pair{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-sm" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
            <thead>
              <tr className="border-b border-gray-800">
                {[
                  { label: 'Tenant',      sortKey: 'tenantId' },
                  { label: 'Group',       sortKey: 'groupDisplayName' },
                  { label: 'Application', sortKey: 'applicationDisplayName' },
                  { label: 'Enabled',     sortKey: 'enabled' },
                  { label: 'Note',        sortKey: null },
                  { label: 'Added By',    sortKey: 'addedBy' },
                  { label: 'Date',        sortKey: 'addedUtc' },
                  { label: 'Actions',     sortKey: null },
                ].map((col, colIdx) => (
                  <th
                    key={col.label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey) : undefined}
                    className={`${colIdx === 7 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none ${col.sortKey ? 'cursor-pointer hover:text-gray-300' : ''}`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{col.label}</span>
                    {col.sortKey && <SortIndicator k={col.sortKey} />}
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => { e.stopPropagation(); startResize(e, colIdx); }}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading allowlist...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <UserGroupIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No allowlisted pairs</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || tenantFilter !== 'all' || !showDisabled
                        ? 'Try adjusting your filters.'
                        : isAdmin ? 'Add a (group, application) pair above.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const compositeKey = `${r.partitionKey}::${r.rowKey}`;
                const isEditing = editingId === compositeKey;
                const isEnabled = r.enabled !== false;
                return (
                <tr key={compositeKey} className={`hover:bg-gray-800/50 transition-colors ${isEnabled ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 text-gray-200 truncate overflow-hidden" title={r.tenantId}>
                    {r.tenantId === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">All tenants (*)</span>
                    ) : (
                      tenantNameById[r.tenantId] || r.tenantId
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300 truncate overflow-hidden" title={r.groupDisplayName}>
                    {r.groupDisplayName || <span className="text-gray-600 italic">(no name)</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-300 truncate overflow-hidden" title={`${r.applicationDisplayName || ''} ${r.applicationId || ''}`}>
                    {r.applicationId === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">Any app (*)</span>
                    ) : isEditing ? (
                      <input type="text" value={editAppName} onChange={(e) => setEditAppName(e.target.value)}
                        placeholder="Display name" className="input-field w-full text-xs py-1" />
                    ) : (
                      <div className="min-w-0">
                        <div className="truncate">{r.applicationDisplayName || <span className="text-gray-600 italic">(no name)</span>}</div>
                        <div className="font-mono text-[10px] text-gray-600 truncate">{r.applicationId}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <button
                        onClick={() => handleToggleEnabled(r)}
                        disabled={togglingId === compositeKey}
                        title={isEnabled ? 'Click to disable' : 'Click to enable'}
                        className="focus:outline-none"
                      >
                        <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                      </button>
                    ) : (
                      <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.note || ''}>
                    {isEditing ? (
                      <input type="text" value={editNote} onChange={(e) => setEditNote(e.target.value)} className="input-field w-full text-xs py-1" autoFocus />
                    ) : (
                      r.note || '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>{r.addedBy || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">{formatDate(r.addedUtc)}</td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'—'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleSaveEdit(r)} disabled={savingEdit} className="text-gray-600 hover:text-green-400 transition-colors p-1" title="Save">
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEdit} disabled={savingEdit} className="text-gray-600 hover:text-gray-300 transition-colors p-1" title="Cancel">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === compositeKey ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleRemove(r)} disabled={removing} className="btn-danger px-2 py-1 text-xs">
                          {removing ? 'Disabling...' : 'Confirm'}
                        </button>
                        <button onClick={() => setConfirmRemove(null)} className="btn-secondary px-2 py-1 text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(r)} className="text-gray-600 hover:text-blue-400 transition-colors p-1" title="Edit note / app display name (Tenant / Group / App are immutable — delete and re-add to change them)">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmRemove(compositeKey)} disabled={!isEnabled} className="text-gray-600 hover:text-red-400 transition-colors p-1 disabled:opacity-30 disabled:cursor-not-allowed" title={isEnabled ? 'Disable (soft-delete) — the rule will resume alerting on this pair' : 'Already disabled'}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
